import type {
  CustomFieldDefinition,
  CustomFieldGroupDefinition,
  ResourceTypeModel
} from '__types__';

import type {
  EditUnit as FormEditUnit,
  EntitySchema as FormEntitySchema,
  FieldsUnit as FormFieldsUnit,
  ListUnit as FormListUnit,
  ScopeSection as FormScopeSection,
  SelectOption
} from 'v5/core/form-schema/formSchema.types';

/**
 * Entity schema — what units exist, in which section, with which write scope,
 * editable or not. Pure data: no callbacks, no React, no HTTP.
 *
 * Knows nothing of what renders it: the schema form engine
 * (`patterns/forms/schema-form`) and the resource editor build on this
 * module, never the other way round.
 *
 * Conventions (binding): discriminated unions with literal tags, explicit
 * absence (`null`, `{ type: 'not-applicable' }`), closed per-kind maps.
 */

/** Resource membership (contacts ↔ enterprise_workspaces join rows). */
export type ResourceMembership =
  | { type: 'hidden' }
  | { type: 'global' }
  | { type: 'workspaces'; workspaceIds: number[] };

export const RESOURCE_SCALAR_COLUMNS = [
  'nick_name',
  'role',
  'first_name',
  'last_name'
] as const;
/** Core columns edited as plain text (`first_name`/`last_name` when a metastructure lists them separately). */
export type ResourceScalarColumn = (typeof RESOURCE_SCALAR_COLUMNS)[number];

/** A workspace rate card (`Rate` row) a contact rate can link to via `rate_id`. */
export type RateCardOption = {
  id: number;
  name: string;
  description: string;
  currency: string;
  amount: number | null;
  interval: string;
  agencyFee: number | null;
  payrollFee: number | null;
  processingFee: number | null;
};

/** Origin for kinds that can be both a core column and a custom field. */
export type ScalarSource =
  | { type: 'core'; column: ResourceScalarColumn }
  | { type: 'custom'; definition: CustomFieldDefinition };

export const CONTACT_DETAIL_TYPES = [
  'EMAIL',
  'PHONE',
  'SOCIAL',
  'WEBSITE'
] as const;
export type ContactDetailType = (typeof CONTACT_DETAIL_TYPES)[number];

/** The contact's relation (and core field) holding each detail type's rows. */
export const contactDetailColumnByType = {
  EMAIL: 'emails',
  PHONE: 'phone_numbers',
  SOCIAL: 'socials',
  WEBSITE: 'websites'
} as const satisfies { [T in ContactDetailType]: string };
export type ContactDetailColumn =
  (typeof contactDetailColumnByType)[ContactDetailType];

export const MEASUREMENT_UNITS = ['cm', 'm', 'ft', 'in'] as const;
export type MeasurementUnit = (typeof MEASUREMENT_UNITS)[number];

export type FieldSpecBase = {
  /** Stable within a unit: 'core:emails', 'custom:42'. */
  key: string;
  label: string;
  description: string | null;
  required: boolean;
  /** Metastructure/binding lock → field read-only even in an editable unit. */
  locked: boolean;
};

/**
 * Discriminated union on `kind`. Every variant carries exactly the static
 * configuration its codec and its field component need — data only.
 */
export type FieldSpec =
  // --- scalar / custom-field kinds ---
  | (FieldSpecBase & {
      kind: 'text';
      multiline: boolean;
      placeholder: string | null;
      source: ScalarSource;
    })
  | (FieldSpecBase & { kind: 'boolean'; definition: CustomFieldDefinition })
  | (FieldSpecBase & { kind: 'number'; definition: CustomFieldDefinition })
  | (FieldSpecBase & { kind: 'date'; definition: CustomFieldDefinition })
  | (FieldSpecBase & { kind: 'date-range'; definition: CustomFieldDefinition })
  | (FieldSpecBase & {
      kind: 'single-select';
      options: SelectOption[];
      clearable: boolean;
      definition: CustomFieldDefinition;
    })
  | (FieldSpecBase & {
      kind: 'multi-select';
      options: SelectOption[];
      definition: CustomFieldDefinition;
    })
  | (FieldSpecBase & {
      kind: 'measurement';
      unit: MeasurementUnit;
      unitLocked: boolean;
      definition: CustomFieldDefinition;
    })
  | (FieldSpecBase & { kind: 'currency'; definition: CustomFieldDefinition })
  | (FieldSpecBase & {
      kind: 'url';
      placeholder: string | null;
      definition: CustomFieldDefinition;
    })
  | (FieldSpecBase & { kind: 'percentage'; definition: CustomFieldDefinition })
  /** Total inches in `integer_value`; edited as feet + inches. */
  | (FieldSpecBase & { kind: 'height'; definition: CustomFieldDefinition })
  /** `string_value` JSON `{ x, y[, z] }` of `{ amount, unit }`. */
  | (FieldSpecBase & {
      kind: 'dimension';
      axes: 2 | 3;
      units: SelectOption[];
      definition: CustomFieldDefinition;
    })
  /** Legacy MEASUREMENT: `integer_value` amount + `string_value` unit. */
  | (FieldSpecBase & {
      kind: 'measurement-legacy';
      units: SelectOption[];
      definition: CustomFieldDefinition;
    })
  /** NetSuite master project id in `integer_value`; options come from the integration (registry). */
  | (FieldSpecBase & {
      kind: 'netsuite-master-project';
      definition: CustomFieldDefinition;
    })
  // --- resource core kinds ---
  | (FieldSpecBase & { kind: 'name'; layout: 'single' | 'first-last' })
  | (FieldSpecBase & {
      kind: 'pronouns';
      options: Array<{ id: number; value: string }>;
    })
  | (FieldSpecBase & {
      kind: 'contact-details';
      detailType: ContactDetailType;
      typeOptions: SelectOption[];
      /** SOCIAL: recognise the platform from a pasted URL and link the handle. */
      platformRecognition: boolean;
    })
  | (FieldSpecBase & {
      kind: 'tags';
      options: SelectOption[];
      allowCreate: boolean;
    })
  | (FieldSpecBase & {
      kind: 'groups';
      options: GroupOption[];
      resourceTypeId: number;
    })
  /** One location; the list is a ListUnit on the entity. */
  | (FieldSpecBase & { kind: 'location' })
  /** One rate; the list is a ListUnit. Rows linked to a rate card (`rate_id`) display the card and are not editable. */
  | (FieldSpecBase & {
      kind: 'rate';
      currencies: SelectOption[];
      defaultCurrency: string;
      rateCards: RateCardOption[];
      /** Where rate cards are managed; `null` when the viewer can't configure them. */
      rateCardsSettingsPath: string | null;
    })
  /** A reference to another entity, by id; the form's kit knows how to search, show and open that entity. */
  | (FieldSpecBase & {
      kind: 'entity-ref';
      /** The core column holding the id. */
      column: 'organisation_id' | 'represented_by_id';
    } & EntityRefTarget);

/**
 * What an `entity-ref` field can point at, per entity. A new entity is one
 * entry here and one in the host's `EntityRefConfig`.
 */
export type EntityRefTargets = {
  resource: { resourceTypeModels: ResourceTypeModel[] };
};
export type EntityRefEntity = keyof EntityRefTargets;
export type EntityRefTarget = {
  [E in EntityRefEntity]: { entity: E } & EntityRefTargets[E];
}[EntityRefEntity];

/** A resource group to pick, with what its row shows beside the name. */
export type GroupOption = SelectOption & { colour: string | null };

export type FieldKind = FieldSpec['kind'];
export type FieldSpecOf<K extends FieldKind> = Extract<FieldSpec, { kind: K }>;
/** Specs backed by a custom field definition (the only ones that can be scoped / mirrored). */
export type CustomFieldSpec = Extract<
  FieldSpec,
  { definition: CustomFieldDefinition }
>;

/** The resource form's structure, over the resource's field specs. */
export type FieldsUnit = FormFieldsUnit<FieldSpec>;
export type ListUnit = FormListUnit<FieldSpec>;
export type EditUnit = FormEditUnit<FieldSpec>;
export type ScopeSection = FormScopeSection<FieldSpec>;
export type EntitySchema = FormEntitySchema<FieldSpec>;

export const fieldSpecUtils = {
  isCustom(spec: FieldSpec): spec is CustomFieldSpec {
    return 'definition' in spec;
  },
  definitionOf(spec: FieldSpec): CustomFieldDefinition | null {
    if ('definition' in spec) return spec.definition;
    if (spec.kind === 'text' && spec.source.type === 'custom') {
      return spec.source.definition;
    }
    return null;
  }
};
